import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { mensagemDeErro } from '@/lib/erros';
import { CampoSenha } from '@/ui/components/campo-senha';
import { MedidorForcaSenha } from '@/ui/components/medidor-forca-senha';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { Campo } from '@/ui/primitives/input';
import {
  type DefinirSenhaRequest,
  definirSenhaRequestSchema,
  type UsuarioResumo,
} from '@fiscoai/contracts';

import { useAlterarUsuarios } from './api';

/**
 * O admin define uma senha temporária para quem esqueceu a sua. O server marca a troca como
 * obrigatória no próximo acesso e encerra as sessões abertas da pessoa.
 */
export function DialogoRedefinirSenha({
  usuario,
  aoFechar,
}: {
  usuario: UsuarioResumo | null;
  aoFechar: () => void;
}) {
  const { definirSenha } = useAlterarUsuarios();
  const form = useForm<DefinirSenhaRequest>({
    resolver: zodResolver(definirSenhaRequestSchema),
    defaultValues: { senha: '' },
  });
  const senha = useWatch({ control: form.control, name: 'senha' });
  const fechar = () => {
    form.reset();
    aoFechar();
  };
  const enviar = form.handleSubmit((dados) => {
    if (!usuario) return;
    definirSenha.mutate(
      { id: usuario.id, senha: dados.senha },
      {
        onSuccess: () => {
          toast.success(`Senha de ${usuario.nome} redefinida`, {
            description: 'Passe a senha temporária: a pessoa cria a própria no próximo acesso.',
          });
          fechar();
        },
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  });
  const titulo = usuario ? `Redefinir senha de ${usuario.nome}` : 'Redefinir senha';
  return (
    <Dialog
      aberto={usuario !== null}
      aoMudar={(aberto) => {
        if (!aberto) fechar();
      }}
      titulo={titulo}
      descricao="As sessões abertas são encerradas e a pessoa cria a própria senha no próximo acesso."
      acoes={
        <>
          <Button variante="fantasma" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            variante="primario"
            carregando={definirSenha.isPending}
            onClick={() => void enviar()}
          >
            Redefinir senha
          </Button>
        </>
      }
    >
      <form
        noValidate
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        {/* Para o gerenciador de senhas associar a senha à conta certa. */}
        <input type="text" hidden readOnly autoComplete="username" value={usuario?.email ?? ''} />
        <Campo
          rotulo="Senha temporária"
          ajuda="Pelo menos 12 caracteres."
          erro={form.formState.errors.senha?.message}
        >
          {(a) => (
            <>
              <CampoSenha autoComplete="new-password" {...a} {...form.register('senha')} />
              <MedidorForcaSenha senha={senha} />
            </>
          )}
        </Campo>
      </form>
    </Dialog>
  );
}
